/**
 * Browser page translation (Google/Edge) detaches React-managed text nodes and re-inserts them
 * inside `<font>` wrappers. When React later calls `removeChild` / `insertBefore` with a node whose
 * parent changed, the browser throws `NotFoundError` and the whole page crashes.
 *
 * These two calls are the only DOM operations React performs that can throw this error, so making
 * them tolerant of a mismatched parent keeps the page translatable without crashing.
 *
 * The guard is installed lazily: nothing is patched until the browser marks the page as translated.
 */
;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();const TRANSLATED_CLASS_NAMES = ['translated-ltr', 'translated-rtl'];

/**
 * Google Translate adds a `translated-*` class to `<html>`; Edge only rewrites `<html lang>` to the
 * target language. Both are treated as "page is translated".
 */
function isPageTranslated(originalLang: string) {
  const { classList, lang } = document.documentElement;
  if (TRANSLATED_CLASS_NAMES.some((className) => classList.contains(className))) return true;
  return lang !== originalLang;
}

function patchDomMutationMethods() {
  const nodePrototype = Node.prototype;
  const originalRemoveChild = nodePrototype.removeChild;
  const originalInsertBefore = nodePrototype.insertBefore;

  nodePrototype.removeChild = function removeChildGuarded<T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) return child;
    return originalRemoveChild.call(this, child) as T;
  };

  nodePrototype.insertBefore = function insertBeforeGuarded<T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) return newNode;
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}

export function installBrowserTranslationGuard() {
  if (typeof window === 'undefined') return;

  const originalLang = document.documentElement.lang;

  if (isPageTranslated(originalLang)) {
    patchDomMutationMethods();
    return;
  }

  const observer = new MutationObserver(() => {
    if (!isPageTranslated(originalLang)) return;
    observer.disconnect();
    patchDomMutationMethods();
  });

  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class', 'lang'],
  });
}
